import React, { useState, useRef } from "react";
import { postApi } from "../../../services/api/postApi";
import useMentionAutocomplete from "../../../hooks/useMentionAutocomplete";
import MentionDropdown from "../../common/MentionDropdown";
import "./PostForms.css";

const TextPostForm = ({ onSuccess, onCancel }) => {
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);

  // @mention autocomplete
  const {
    suggestions,
    showDropdown,
    loading: mentionLoading,
    handleInputChange,
    insertMention,
    closeDropdown,
    resetMentions,
  } = useMentionAutocomplete();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      alert("Please enter some content");
      return;
    }

    setLoading(true);
    try {
      const response = await postApi.createPost({
        postType: "text",
        content: content.trim(),
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        visibility: visibility,
      });

      onSuccess(response.data.post);
      setContent("");
      setTags("");
      resetMentions();
    } catch (error) {
      alert("Failed to create post");
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    
    // Handle @mention detection
    const cursorPosition = e.target.selectionStart;
    handleInputChange(newContent, cursorPosition);
  };

  const handleMentionSelect = (user) => {
    const cursorPosition = textareaRef.current?.selectionStart || content.length;
    const result = insertMention(user, content, cursorPosition);
    
    if (result) {
      setContent(result.text);
      
      // Set cursor position after the inserted mention
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(result.cursorPosition, result.cursorPosition);
        }
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    // Don't interfere when dropdown is open
    if (showDropdown && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === "Tab")) {
      return;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="post-form">
      <div className="form-group">
        <label htmlFor="content">What's on your mind?</label>
        <div className="form-group__textarea-wrapper">
          <textarea
            ref={textareaRef}
            id="content"
            value={content}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Share your thoughts... Type @ to mention someone"
            rows={6}
            maxLength={5000}
            required
          />
          
          {/* @mention dropdown */}
          {showDropdown && (
            <MentionDropdown
              suggestions={suggestions}
              onSelect={handleMentionSelect}
              onClose={closeDropdown}
              loading={mentionLoading}
            />
          )}
        </div>
        <div className="char-count">{content.length}/5000</div>
      </div>

      <div className="form-group">
        <label htmlFor="tags">Tags (optional)</label>
        <input
          type="text"
          id="tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="AI, research, machine-learning (comma-separated)"
        />
        <small className="form-hint">
          Add tags to help others discover your post
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="visibility">Visibility</label>
        <select
          id="visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
        >
          <option value="public">Public</option>
          <option value="connections">Connections Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !content.trim()}
          className="submit-btn"
        >
          {loading ? "Posting..." : "Post"}
        </button>
      </div>
    </form>
  );
};

export default TextPostForm;
